import React from "react";

interface SlideContent {
  title?: string;
  subtitle?: string;
  content?: string;
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

const Entrance2Slide = {
  id: "entrance2-slide",
  name: "Entrance Slide (Ocean Waves)",
  fields: [
    {
      id: "title",
      label: "Title",
      type: "text",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
    },
    {
      id: "content",
      label: "Intro Note",
      type: "text",
    },
  ],
  render: (content: SlideContent) => {
    const {
      title = "",
      subtitle = "",
      content: introNote = "",
      bgColor,
      onEdit = () => {},
    } = content;

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);

      if (window.updateLiveConfig) {
        window.updateLiveConfig(updatedContent);
      }
    };

    return (
      <div
        className="w-full h-full flex flex-col items-center justify-center text-white relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900"
        style={{ background: bgColor }}
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-blue-400/10 to-transparent" />
          <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-blue-900/20 to-transparent" />
        </div>

        {/* Content Container */}
        <div className="relative z-10 max-w-4xl mx-auto p-8 text-center">
          {/* Main Title */}
          <div
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) =>
              handleEdit("title")(e.currentTarget.textContent || "")
            }
            className="text-7xl font-bold mb-6 text-white animate-fade-in-up cursor-text"
          >
            {title || "Enter title..."}
          </div>

          {/* Subtitle */}
          <div
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) =>
              handleEdit("subtitle")(e.currentTarget.textContent || "")
            }
            className="text-3xl font-light mb-12 text-blue-100 animate-fade-in-up animation-delay-200 cursor-text"
          >
            {subtitle || "Enter subtitle..."}
          </div>

          {/* Intro Note */}
          <div
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) =>
              handleEdit("content")(e.currentTarget.textContent || "")
            }
            className="text-xl text-blue-50 max-w-2xl mx-auto animate-fade-in-up animation-delay-400 cursor-text"
          >
            {introNote || "Enter intro note..."}
          </div>
        </div>

        {/* Decorative Wave Effect */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            className="waves"
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            viewBox="0 24 150 28"
            preserveAspectRatio="none"
            shapeRendering="auto"
          >
            <defs>
              <path
                id="gentle-wave"
                d="M-160 44c30 0 58-18 88-18s 58 18 88 18 58-18 88-18 58 18 88 18 v44h-352z"
              />
            </defs>
            <g className="parallax">
              <use
                xlinkHref="#gentle-wave"
                x="48"
                y="0"
                fill="rgba(255,255,255,0.7)"
              />
              <use
                xlinkHref="#gentle-wave"
                x="48"
                y="3"
                fill="rgba(255,255,255,0.5)"
              />
              <use
                xlinkHref="#gentle-wave"
                x="48"
                y="5"
                fill="rgba(255,255,255,0.3)"
              />
              <use xlinkHref="#gentle-wave" x="48" y="7" fill="#fff" />
            </g>
          </svg>
        </div>
      </div>
    );
  },
};

const style = document.createElement("style");
style.textContent = `
  .waves {
    width: 100%;
    height: 15vh;
    margin-bottom: -7px;
    min-height: 100px;
    max-height: 150px;
  }
  .parallax > use {
    animation: move-forever 25s cubic-bezier(0.55, 0.5, 0.45, 0.5) infinite;
  }
  .parallax > use:nth-child(1) {
    animation-delay: -2s;
    animation-duration: 7s;
  }
  .parallax > use:nth-child(2) {
    animation-delay: -3s;
    animation-duration: 10s;
  }
  .parallax > use:nth-child(3) {
    animation-delay: -4s;
    animation-duration: 13s;
  }
  .parallax > use:nth-child(4) {
    animation-delay: -5s;
    animation-duration: 20s;
  }
  @keyframes move-forever {
    0% {
      transform: translate3d(-90px, 0, 0);
    }
    100% {
      transform: translate3d(85px, 0, 0);
    }
  }
`;
document.head.appendChild(style);

declare global {
  interface Window {
    updateLiveConfig?: (config: SlideContent) => void;
  }
}

export default Entrance2Slide;
